import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ArrowRight, Rocket, TrendingUp, Shield, Sparkles, Zap, Star, Car as CarIcon, Users, Fuel, Award } from "lucide-react";
import { api, formatINR } from "@/lib/api";
import { SEO } from "@/components/common/SEO";
import CarCard from "@/components/common/CarCard";
import SmartImage from "@/components/common/SmartImage";

const BUDGETS = [
  { label: "Under ₹5L", max: 500000 }, { label: "Under ₹10L", max: 1000000 },
  { label: "Under ₹15L", max: 1500000 }, { label: "Under ₹20L", max: 2000000 },
  { label: "Under ₹30L", max: 3000000 },
];

// Fallback hero slides (used only if the CMS returns none)
const FALLBACK_HERO_SLIDES = [
  {
    id: "brand",
    eyebrow: "Trusted by Indian car buyers",
    eyebrow_icon: "star",
    title: "India's Smartest",
    title_accent: "Car Buying Platform",
    subtitle: "Compare cars, read expert reviews, discover new launches and calculate ownership costs — everything you need to buy your next car with confidence.",
    primary_cta_label: "Explore Cars", primary_cta_to: "/cars",
    secondary_cta_label: "Ask an Expert", secondary_cta_to: "/chat",
    image: "https://images.unsplash.com/photo-1503376780353-7e6692767b70?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
    accent_gradient: "from-white via-[#EFF6FF] to-[#ECFEFF]",
  },
];

const HERO_ICON = { star: Star, zap: Zap, award: Award, sparkles: Sparkles };
const CATS = [
  { label: "SUV", key: "SUV" }, { label: "Hatchback", key: "Hatchback" },
  { label: "Sedan", key: "Sedan" }, { label: "MPV / 7-Seater", key: "MPV" },
  { label: "EV", key: "EV", fuel: true }, { label: "Hybrid", key: "Hybrid", fuel: true },
  { label: "Luxury", key: "Luxury", luxury: true }, { label: "Compact SUV", key: "SUV" },
];
const POPULAR_COMPARES = [
  ["tata-nexon", "hyundai-creta"], ["maruti-suzuki-swift", "hyundai-i20"],
  ["mahindra-xuv700", "tata-harrier"], ["kia-seltos", "hyundai-creta"],
];
// Best-of links are now loaded from /api/settings

// Fallback brand strip — used only if the CMS returns no featured brands
const FALLBACK_BRAND_STRIP = [
  { slug: "maruti-suzuki", name: "Maruti Suzuki",  cls: "font-semibold tracking-tight" },
  { slug: "tata",          name: "TATA",           cls: "font-bold tracking-widest" },
  { slug: "mahindra",      name: "Mahindra",       cls: "font-semibold italic tracking-tight" },
  { slug: "hyundai",       name: "HYUNDAI",        cls: "font-medium tracking-[0.3em]" },
  { slug: "kia",           name: "KIA",            cls: "font-black tracking-[0.25em]" },
  { slug: "toyota",        name: "TOYOTA",         cls: "font-semibold tracking-[0.2em]" },
  { slug: "honda",         name: "HONDA",          cls: "font-bold tracking-[0.15em]" },
  { slug: "mg",            name: "MG",             cls: "font-black tracking-[0.3em]" },
];

const FALLBACK_TRUST = [
  { label: "Cars", value: "500+" },
  { label: "Comparisons", value: "2,000+" },
  { label: "Expert Reviews", value: "Weekly" },
  { label: "News", value: "Daily" },
];

export default function Home() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [cars, setCars] = useState([]);
  const [evCars, setEvCars] = useState([]);
  const [articles, setArticles] = useState([]);
  const [launches, setLaunches] = useState([]);
  const [trending, setTrending] = useState([]);
  const [mostCompared, setMostCompared] = useState([]);
  const [heroSlides, setHeroSlides] = useState(FALLBACK_HERO_SLIDES);
  const [featuredBrands, setFeaturedBrands] = useState([]);
  const [comparisons, setComparisons] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.get("/cars?limit=8").then((r) => setCars(r.data));
    api.get("/cars?fuel=EV&limit=12").then((r) => {
      const list = r.data || [];
      const featuredSlugs = ["tata-harrier-ev", "tata-nexon-ev", "mg-zs-ev", "byd-atto-3", "kia-ev6"];
      const featured = featuredSlugs.map(s => list.find(c => c.slug === s)).filter(Boolean);
      const rest = list.filter(c => !featuredSlugs.includes(c.slug));
      setEvCars([...featured, ...rest].slice(0, 4));
    });
    api.get("/articles?limit=6").then((r) => setArticles(r.data));
    api.get("/launches?limit=6").then((r) => setLaunches(r.data));
    api.get("/cars/trending?limit=8").then((r) => setTrending(r.data)).catch(() => {});
    api.get("/cars/most-compared?limit=6").then((r) => setMostCompared(r.data)).catch(() => {});
    api.get("/hero-slides").then((r) => { if (r.data?.length) setHeroSlides(r.data); }).catch(() => {});
    api.get("/brands?featured=true").then((r) => setFeaturedBrands(r.data || [])).catch(() => {});
    api.get("/comparisons?limit=6").then((r) => setComparisons(r.data || [])).catch(() => {});
    api.get("/reviews?limit=3").then((r) => setReviews(r.data || [])).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const onSearch = (e) => { e.preventDefault(); navigate(q.trim() ? `/cars?q=${encodeURIComponent(q.trim())}` : "/cars"); };
  const guides = articles.filter(a => a.category === "Buyer Guide" || a.category === "Guide" || a.category === "Buying Guide");
  const news = articles.filter(a => a.category === "News");
  const editorsPicks = cars.slice(2, 6);

  const trustStats = settings?.trust_stats?.length ? settings.trust_stats : FALLBACK_TRUST;
  const bestOfLinks = settings?.best_of_links?.length ? settings.best_of_links : [
    { slug: "cars-under-10-lakh", label: "Best cars under ₹10L" },
    { slug: "suvs", label: "Best SUVs in India" },
    { slug: "electric-cars", label: "Best electric cars" },
    { slug: "7-seaters", label: "Best 7-seaters" },
  ];
  const brandStrip = featuredBrands.length
    ? featuredBrands.map((b) => ({ slug: b.slug, name: b.name.toUpperCase(), cls: "font-semibold tracking-[0.2em]" }))
    : FALLBACK_BRAND_STRIP;

  return (
    <div data-testid="home-page">
      <SEO title="TrendingNewCar — India's Smartest Car Buying Platform"
        description="Compare cars, read expert reviews, discover new launches, calculate ownership cost, and get intelligent recommendations — all in one place."
        image="https://images.unsplash.com/photo-1781771531096-099f8138df00"/>

      {/* HERO — rotating banner, fits within one viewport */}
      <HeroRotator navigate={navigate} q={q} setQ={setQ} onSearch={onSearch} slides={heroSlides} trustStats={trustStats}/>

      {/* BRAND STRIP */}
      <section className="border-y border-[#E5E7EB] section-tint" data-testid="section-brand-strip">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
          <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground text-center mb-6">
            Featuring cars from India&apos;s most-loved brands
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-4 sm:gap-x-10">
            {brandStrip.map((b) => (
              <Link key={b.slug} to={`/brands/${b.slug}`} data-testid={`brand-${b.slug}`}
                className={`text-base sm:text-lg text-muted-foreground hover:text-[#2563EB] transition-colors ${b.cls || "font-semibold tracking-[0.2em]"}`}>
                {b.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* TRENDING THIS WEEK (live view counters) */}
      <Section eyebrow="Trending this week" title="What India is looking at right now" cta={{to:"/cars", label:"All cars"}} testId="section-trending" bg="white">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {(trending.length ? trending : cars).slice(0,4).map((c,i) => (
            <div key={c.slug} className="relative">
              {i < 2 && <span className="absolute top-3 left-3 z-10 text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-emerald-500 text-white font-semibold shadow"><TrendingUp className="w-3 h-3 inline -mt-0.5 mr-1"/>Trending</span>}
              <CarCard car={c}/>
            </div>
          ))}
        </div>
      </Section>

      {/* MOST COMPARED */}
      {mostCompared.length > 0 && (
        <Section eyebrow="Head to head" title="Most compared this week" cta={{to:"/compare", label:"Open compare"}} testId="section-most-compared" bg="tint">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {mostCompared.slice(0,6).reduce((rows, c, i, arr) => {
              if (i % 2 === 0) rows.push([c, arr[i+1]].filter(Boolean));
              return rows;
            }, []).map(([a,b]) => b ? (
              <Link key={`${a.slug}-vs-${b.slug}`} to={`/compare?slugs=${a.slug},${b.slug}`}
                data-testid={`mc-${a.slug}-${b.slug}`}
                className="rounded-2xl border border-border p-5 bg-card hover:border-blue-400 hover:shadow-lg transition-all flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{a.brand_name}</div>
                  <div className="font-display text-lg tracking-tight truncate">{a.model}</div>
                </div>
                <span className="text-xs px-2 py-1 rounded-full bg-secondary text-muted-foreground">vs</span>
                <div className="flex-1 min-w-0 text-right">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{b.brand_name}</div>
                  <div className="font-display text-lg tracking-tight truncate">{b.model}</div>
                </div>
              </Link>
            ) : null)}
          </div>
        </Section>
      )}

      {/* LATEST LAUNCHES */}
      <Section eyebrow="Just In" title="Latest launches" cta={{to:"/launches", label:"See all launches"}} testId="section-launches" bg="white">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {launches.slice(0,3).map((l)=>{
            const days = Math.max(0, Math.round((new Date(l.expected_date)-new Date())/86400000));
            const status = days === 0 ? "Launching today" : days <= 30 ? `${days} days to launch` : `Expected in ${days} days`;
            return (
              <div key={l.model} data-testid={`launch-${l.model.toLowerCase().replace(/\s+/g,'-')}`}
                className="group card-premium overflow-hidden">
                <div className="aspect-[16/10] overflow-hidden relative bg-[#F3F6FA]">
                  <SmartImage src={l.teaser_image} alt={l.model} label={l.model}
                    className="group-hover:scale-[1.05] transition-transform duration-700 ease-out"/>
                  <div className="absolute top-3 right-3 text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full bg-white/95 backdrop-blur text-blue-700 font-semibold shadow-sm">
                    <Rocket className="w-3 h-3 inline -mt-0.5 mr-1"/>{status}
                  </div>
                </div>
                <div className="p-5">
                  <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{l.brand} · {l.body_type}</div>
                  <div className="font-display text-xl mt-2">{l.model}</div>
                  <div className="mt-3 flex items-center justify-between text-sm">
                    <span className="font-mono">{formatINR(l.expected_price_min)}</span>
                    <span className="text-xs text-muted-foreground">Expected {new Date(l.expected_date).toLocaleDateString("en-IN", {month:"short", year:"numeric"})}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      {/* POPULAR COMPARISONS (CMS-managed) */}
      {(comparisons.length > 0 || POPULAR_COMPARES.length > 0) && (
        <Section eyebrow="Head to head" title="Popular comparisons" cta={{to:"/compare", label:"Open compare"}} testId="section-compares" bg="alt">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {comparisons.length > 0
              ? comparisons.slice(0,4).map((c) => (
                <Link key={c.slug} to={`/compare-cars/${c.slug}`} data-testid={`cms-compare-${c.slug}`}
                  className="rounded-2xl border border-border p-5 bg-card hover:border-blue-400 hover:shadow-lg transition-all">
                  <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Comparison</div>
                  <div className="font-display text-lg mt-2 tracking-tight leading-snug line-clamp-3">{c.title}</div>
                  <div className="mt-4 text-sm text-blue-600 inline-flex items-center gap-1">Read verdict <ArrowRight className="w-3 h-3"/></div>
                </Link>
              ))
              : POPULAR_COMPARES.map(([a,b]) => (
                <Link key={`${a}-${b}`} to={`/compare?slugs=${a},${b}`} data-testid={`pop-compare-${a}-${b}`} className="rounded-2xl border border-border p-5 bg-card hover:border-blue-400 hover:shadow-lg transition-all">
                  <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">vs</div>
                  <div className="font-display text-lg mt-2 tracking-tight capitalize">{a.replace(/-/g," ")}<br/><span className="text-muted-foreground text-sm">vs</span><br/>{b.replace(/-/g," ")}</div>
                  <div className="mt-4 text-sm text-blue-600 dark:text-blue-400 inline-flex items-center gap-1">Compare <ArrowRight className="w-3 h-3"/></div>
                </Link>
              ))
            }
          </div>
        </Section>
      )}

      {/* EXPERT REVIEWS (CMS Review docs) */}
      {reviews.length > 0 && (
        <Section eyebrow="Editorial" title="Expert reviews" cta={{to:"/reviews", label:"All reviews"}} testId="section-reviews" bg="white">
          <div className="grid md:grid-cols-3 gap-5">{reviews.slice(0,3).map(r => (
            <Link key={r.slug} to={`/reviews/${r.slug}`} data-testid={`review-${r.slug}`} className="card-premium overflow-hidden group flex flex-col">
              {r.hero_image && (
                <div className="aspect-[16/9] overflow-hidden bg-[#F3F6FA]">
                  <SmartImage src={r.hero_image} alt={r.title} label={r.title} className="group-hover:scale-[1.05] transition-transform duration-700 ease-out"/>
                </div>
              )}
              <div className="p-5">
                <div className="flex items-center gap-1 text-amber-500 text-sm">
                  <Star className="w-4 h-4 fill-current"/> <span className="font-mono font-semibold">{Number(r.expert_rating).toFixed(1)}</span>
                  <span className="text-muted-foreground text-xs ml-1">/ 10</span>
                </div>
                <div className="font-display text-lg mt-2 tracking-tight leading-snug line-clamp-2">{r.title}</div>
                <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{r.verdict}</p>
              </div>
            </Link>
          ))}</div>
        </Section>
      )}

      {/* CARS BY BUDGET */}
      <Section eyebrow="Shop by budget" title="Find your perfect price bracket" testId="section-budgets" bg="tint">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {BUDGETS.map(b=>(
            <Link key={b.label} to={`/cars?price_max=${b.max}`} data-testid={`budget-${b.max}`} className="rounded-2xl border border-border bg-card p-5 hover:border-blue-400 hover:shadow-md transition-all group">
              <div className="text-sm text-muted-foreground">Cars</div>
              <div className="font-display text-xl mt-1">{b.label}</div>
              <div className="mt-3 text-xs text-blue-600 inline-flex items-center gap-1 group-hover:gap-2 transition-all">Browse <ArrowRight className="w-3 h-3"/></div>
            </Link>
          ))}
        </div>
      </Section>

      {/* CARS BY CATEGORY */}
      <Section eyebrow="Shop by category" title="Browse by body & fuel type" testId="section-categories" bg="white">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {CATS.map(c => {
            const to = c.fuel ? `/cars?fuel=${c.key}` : c.luxury ? `/cars?price_min=4000000` : `/cars?body_type=${c.key}`;
            return (
              <Link key={c.label} to={to} data-testid={`cat-${c.label}`} className="rounded-2xl border border-border bg-card p-5 hover:border-blue-400 hover:shadow-md transition-all">
                <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-950 grid place-items-center text-blue-600 dark:text-blue-400 mb-3"><CarIcon className="w-4 h-4"/></div>
                <div className="font-display text-lg">{c.label}</div>
              </Link>
            );
          })}
        </div>
      </Section>

      {/* BEST OF (ranked lists) */}
      <Section eyebrow="Ranked lists" title="Curated best-of guides" cta={{to:"/cars", label:"All cars"}} testId="section-bestof" bg="alt">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {bestOfLinks.map(b => (
            <Link key={b.slug} to={`/best/${b.slug}`} data-testid={`bestof-link-${b.slug}`}
              className="rounded-2xl border border-border bg-card p-4 hover:border-blue-400 hover:shadow-md transition-all group">
              <Award className="w-5 h-5 text-blue-600 mb-2"/>
              <div className="text-sm font-medium leading-snug">{b.label}</div>
              <div className="mt-2 text-xs text-blue-600 inline-flex items-center gap-1 group-hover:gap-2 transition-all">See list <ArrowRight className="w-3 h-3"/></div>
            </Link>
          ))}
        </div>
      </Section>

      {/* EDITOR'S CHOICE */}
      {editorsPicks.length > 0 && (
        <Section eyebrow="Handpicked" title="Editor's Choice this week" testId="section-editors-picks" bg="white">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {editorsPicks.map(c => (
              <div key={c.slug} className="relative">
                <span className="absolute top-3 left-3 z-10 text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full bg-gradient-to-r from-blue-600 to-emerald-500 text-white font-semibold shadow-md">
                  <Sparkles className="w-3 h-3 inline -mt-0.5 mr-1"/>Editor&apos;s Choice
                </span>
                <CarCard car={c}/>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* EV ZONE — very light blue tint per brief */}
      {evCars.length > 0 && (
        <section className="border-y border-[#E5E7EB] bg-gradient-to-b from-[#EFF6FF] to-[#ECFEFF] dark:from-transparent dark:to-transparent" data-testid="section-ev-zone">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 reveal">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-teal-600 dark:text-teal-400 font-semibold flex items-center gap-1"><Zap className="w-3 h-3"/> EV Zone</div>
                <h2 className="font-display text-3xl sm:text-4xl mt-2 tracking-tight text-[#1F2937] dark:text-foreground">The electric future, made in India.</h2>
                <p className="text-muted-foreground mt-3 text-sm max-w-lg">From the Tata Harrier EV&apos;s 627 km range to Mahindra&apos;s born-electric platform — India&apos;s EV story is accelerating fast.</p>
              </div>
              <Link to="/cars?fuel=EV" data-testid="ev-zone-link" className="text-sm text-teal-700 dark:text-teal-400 hover:underline">All EVs →</Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">{evCars.map(c=><CarCard key={c.slug} car={c}/>)}</div>
          </div>
        </section>
      )}

      {/* BUYING GUIDES */}
      {guides.length > 0 && (
        <Section eyebrow="Learn before you buy" title="Buying guides" cta={{to:"/news?category=Buyer%20Guide", label:"All guides"}} testId="section-guides" bg="alt">
          <div className="grid md:grid-cols-3 gap-6">{guides.slice(0,3).map(a=><ArticleCard key={a.slug} a={a}/>)}</div>
        </Section>
      )}

      {/* LATEST NEWS */}
      {news.length > 0 && (
        <Section eyebrow="News" title="Latest from the industry" cta={{to:"/news", label:"All news"}} testId="section-news" bg="tint">
          <div className="grid md:grid-cols-3 gap-6">{news.slice(0,3).map(a=><ArticleCard key={a.slug} a={a}/>)}</div>
        </Section>
      )}

      {/* AI ASSISTANT — subtle, not dominant */}
      <section className="section-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 reveal">
          <div className="rounded-[28px] border border-[#E5E7EB] bg-gradient-to-b from-white via-[#F8FAFC] to-[#EFF6FF] dark:from-transparent dark:via-transparent dark:to-transparent p-10 sm:p-14 text-center shadow-[0_8px_24px_rgba(15,23,42,0.06)]" data-testid="ai-cta">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-blue-200 dark:border-blue-800 bg-white text-xs uppercase tracking-[0.2em] text-blue-700 dark:text-blue-300"><Sparkles className="w-3 h-3"/> AI Assistant</div>
            <h2 className="font-display text-3xl sm:text-5xl mt-4 tracking-tighter text-[#1F2937] dark:text-foreground">Still confused?</h2>
            <p className="font-display text-2xl sm:text-3xl mt-1 tracking-tight text-muted-foreground">Ask our automobile expert.</p>
            <p className="max-w-lg mx-auto text-muted-foreground mt-4 text-sm">Chat with our AI assistant for tailored recommendations, comparisons and honest answers about any car in India.</p>
            <div className="mt-8 flex justify-center gap-3 flex-wrap">
              <Link to="/chat" data-testid="cta-ask-expert" className="btn-primary h-12 px-6 text-sm font-medium inline-flex items-center gap-2">Ask an Expert <ArrowRight className="w-4 h-4"/></Link>
              <Link to="/advisor" data-testid="cta-advisor" className="btn-secondary h-12 px-6 text-sm font-medium inline-flex items-center gap-2">Get Recommendations</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// Category badge presets — kept in sync with HeroSlidesAdmin.BADGE_PRESETS
const BADGE_STYLES = {
  "new-launch":    { emoji: "\ud83d\ude97", label: "New Launch",       cls: "bg-blue-50 text-blue-700 border-blue-200" },
  "expert-review": { emoji: "\u2b50",       label: "Expert Review",    cls: "bg-amber-50 text-amber-800 border-amber-200" },
  "ev-special":    { emoji: "\u26a1",       label: "EV Special",       cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  "trending":      { emoji: "\ud83d\udd25", label: "Trending",         cls: "bg-orange-50 text-orange-700 border-orange-200" },
  "compare":       { emoji: "\u2696\ufe0f", label: "Compare",          cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  "editors-pick":  { emoji: "\ud83c\udfc6", label: "Editor\u2019s Pick", cls: "bg-yellow-50 text-yellow-800 border-yellow-200" },
  "festival-offer":{ emoji: "\ud83c\udf89", label: "Festival Offer",   cls: "bg-pink-50 text-pink-700 border-pink-200" },
  "auto-expo":     { emoji: "\ud83c\udfdf\ufe0f", label: "Auto Expo",  cls: "bg-violet-50 text-violet-700 border-violet-200" },
  "sponsored":     { emoji: "\ud83d\udcc8", label: "Sponsored",        cls: "bg-slate-100 text-slate-700 border-slate-200" },
};

function HeroRotator({ navigate, q, setQ, onSearch, slides = FALLBACK_HERO_SLIDES, trustStats = FALLBACK_TRUST }) {
  const [idx, setIdx] = React.useState(0);
  const [paused, setPaused] = React.useState(false);
  const touchStart = React.useRef(null);
  const n = slides.length || 1;

  React.useEffect(() => {
    if (paused || n <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % n), 6000);
    return () => clearInterval(t);
  }, [paused, n]);

  React.useEffect(() => { if (idx >= n) setIdx(0); }, [n, idx]);

  const s = slides[Math.min(idx, n - 1)] || slides[0];
  const iconKey = s.eyebrow_icon || s.eyebrowIcon || "sparkles";
  const EyebrowIcon = HERO_ICON[iconKey] || Star;
  const accent = s.accent_gradient || s.accent || "from-white via-[#EFF6FF] to-[#ECFEFF]";
  const primaryLabel = s.primary_cta_label || s.primaryCta?.label || "Explore";
  const primaryTo = s.primary_cta_to || s.primaryCta?.to || "/cars";
  const secondaryLabel = s.secondary_cta_label || s.secondaryCta?.label;
  const secondaryTo = s.secondary_cta_to || s.secondaryCta?.to;
  const titleAccent = s.title_accent || s.titleAccent || "";
  const slideKey = s.id || s._id || `${s.order ?? idx}-${s.title}`;
  const badge = s.badge_key ? BADGE_STYLES[s.badge_key] : null;
  const badgeLabel = s.badge_label || badge?.label || s.eyebrow;

  const go = (delta) => setIdx((i) => (i + delta + n) % n);
  const onTouchStart = (e) => { touchStart.current = e.touches?.[0]?.clientX ?? null; };
  const onTouchEnd = (e) => {
    if (touchStart.current == null) return;
    const dx = (e.changedTouches?.[0]?.clientX ?? 0) - touchStart.current;
    touchStart.current = null;
    if (Math.abs(dx) < 40) return;
    go(dx < 0 ? 1 : -1);
  };

  return (
    <section
      data-testid="hero-rotator"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      className="relative overflow-hidden"
      style={{ minHeight: "calc(100svh - 4rem)" }}
    >
      {/* rotating gradient */}
      {slides.map((slide, i) => (
        <div key={slide.id || slide._id || `${slide.order ?? i}-${slide.title}`} aria-hidden="true"
          className={`absolute inset-0 bg-gradient-to-b ${slide.accent_gradient || slide.accent || accent} transition-opacity duration-1000 dark:from-transparent dark:via-transparent dark:to-transparent`}
          style={{ opacity: i === idx ? 1 : 0 }}/>
      ))}
      <div aria-hidden="true" className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-blue-100/50 blur-3xl dark:bg-blue-500/10"/>
      <div aria-hidden="true" className="absolute -bottom-32 -left-24 w-[380px] h-[380px] rounded-full bg-teal-100/50 blur-3xl dark:bg-teal-500/10"/>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-6 pb-14 lg:pt-8 lg:pb-16 grid lg:grid-cols-12 gap-8 lg:gap-12 items-start h-full min-h-[inherit]">
        {/* Text column */}
        <div className="lg:col-span-7 flex flex-col justify-center" data-testid={`hero-slide-${slideKey}`}>
          {badge ? (
            <span data-testid={`hero-badge-${s.badge_key}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[11px] uppercase tracking-[0.18em] font-medium shadow-sm w-fit ${badge.cls}`}>
              <span className="text-sm leading-none">{badge.emoji}</span> {badgeLabel}
            </span>
          ) : (
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#E5E7EB] bg-white text-[11px] uppercase tracking-[0.2em] text-muted-foreground shadow-sm w-fit">
              <EyebrowIcon className="w-3 h-3 text-blue-600"/> {s.eyebrow}
            </div>
          )}
          <h1 key={slideKey} className="hero-fade mt-5 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-semibold leading-[1.03] text-[#1F2937] dark:text-foreground">
            {s.title}<br/><span className="text-ai">{titleAccent}</span>
          </h1>
          <p key={`p-${slideKey}`} className="hero-fade mt-5 text-base sm:text-lg text-[#4B5563] dark:text-muted-foreground max-w-xl leading-relaxed">
            {s.subtitle}
          </p>

          <form onSubmit={onSearch} className="mt-6 max-w-2xl">
            <div className="bg-white dark:bg-card rounded-2xl p-2 flex items-center gap-2 shadow-[0_10px_30px_rgba(37,99,235,0.10)] border border-[#E5E7EB]">
              <Search className="w-5 h-5 text-muted-foreground ml-3 shrink-0"/>
              <input data-testid="hero-prompt-input" value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Search a car, brand or model…" className="flex-1 bg-transparent outline-none py-2.5 text-base placeholder:text-muted-foreground"/>
              <button data-testid="hero-prompt-submit" type="submit" className="btn-primary h-10 px-5 font-medium text-sm inline-flex items-center gap-2">Search <ArrowRight className="w-4 h-4"/></button>
            </div>
            <div className="mt-4 flex flex-wrap gap-3">
              <Link to={primaryTo} data-testid="hero-explore-cta" className="btn-primary h-11 px-5 text-sm font-medium inline-flex items-center gap-2">{primaryLabel} <ArrowRight className="w-4 h-4"/></Link>
              {secondaryLabel && secondaryTo && (
                <Link to={secondaryTo} data-testid="hero-compare-cta" className="btn-secondary h-11 px-5 text-sm font-medium inline-flex items-center gap-2">{secondaryLabel}</Link>
              )}
            </div>
          </form>

          {/* Trust indicators (compact) */}
          <div className="mt-6 grid grid-cols-4 gap-4 max-w-xl" data-testid="trust-stats">
            {trustStats.map((t) => (
              <div key={t.label} className="border-l-2 border-blue-500 pl-3 py-0.5">
                <div className="font-display text-lg sm:text-xl tracking-tight text-[#1F2937] dark:text-foreground">{t.value}</div>
                <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mt-0.5">{t.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Image column */}
        <div className="lg:col-span-5 relative">
          <div className="relative aspect-[5/4] lg:aspect-[4/3.4] rounded-[24px] overflow-hidden border border-[#E5E7EB] shadow-[0_30px_60px_-15px_rgba(37,99,235,0.18),0_10px_25px_-10px_rgba(20,184,166,0.12)] bg-white">
            {slides.map((slide, i) => (
              <div
                key={slide.id || slide._id || `${slide.order ?? i}-${slide.title}`}
                className="absolute inset-0 transition-opacity duration-1000"
                style={{ opacity: i === idx ? 1 : 0 }}
              >
                <SmartImage src={slide.image} alt={slide.title} eager={i === 0} label={slide.title}/>
              </div>
            ))}
          </div>

          {/* Prev / Next arrows */}
          <button aria-label="Previous slide" data-testid="hero-prev" onClick={() => go(-1)}
            className="absolute left-3 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/90 backdrop-blur border border-[#E5E7EB] grid place-items-center hover:bg-white shadow-md">
            <ArrowRight className="w-4 h-4 rotate-180"/>
          </button>
          <button aria-label="Next slide" data-testid="hero-next" onClick={() => go(1)}
            className="absolute right-3 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/90 backdrop-blur border border-[#E5E7EB] grid place-items-center hover:bg-white shadow-md">
            <ArrowRight className="w-4 h-4"/>
          </button>
        </div>
      </div>

      {/* Dots */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10" data-testid="hero-dots">
        {slides.map((slide, i) => (
          <button key={slide.id || slide._id || `${slide.order ?? i}-${slide.title}`} data-testid={`hero-dot-${i}`} aria-label={`Go to slide ${i + 1}`} onClick={() => setIdx(i)}
            className={`h-1.5 rounded-full transition-all ${i === idx ? "w-8 bg-[#2563EB]" : "w-2.5 bg-[#CBD5E1] hover:bg-[#94A3B8]"}`}/>
        ))}
      </div>
    </section>
  );
}

function Section({ eyebrow, title, cta, testId, children, bg = "white" }) {
  const bgClass = bg === "tint" ? "section-tint" : bg === "alt" ? "section-alt" : "section-white";
  return (
    <section className={`${bgClass}`} data-testid={testId}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 reveal">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{eyebrow}</div>
            <h2 className="font-display text-3xl sm:text-4xl mt-2 tracking-tight">{title}</h2>
          </div>
          {cta && <Link to={cta.to} className="text-sm text-blue-600 dark:text-blue-400 hover:underline">{cta.label} →</Link>}
        </div>
        {children}
      </div>
    </section>
  );
}
function ArticleCard({ a }) {
  return (
    <Link to={`/news/${a.slug}`} data-testid={`article-${a.slug}`} className="card-premium overflow-hidden group flex flex-col">
      <div className="aspect-[16/9] overflow-hidden bg-[#F3F6FA]"><SmartImage src={a.hero_image} alt={a.title} label={a.title} className="group-hover:scale-[1.05] transition-transform duration-700 ease-out"/></div>
      <div className="p-5">
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{a.category} · {a.read_minutes} min</div>
        <div className="font-display text-lg mt-2 tracking-tight leading-snug line-clamp-2">{a.title}</div>
        <div className="text-sm text-muted-foreground mt-2 line-clamp-2">{a.excerpt}</div>
      </div>
    </Link>
  );
}
